/* Resume templates (8): Classic, Modern, Minimal, Executive, ATS Friendly, Corporate,
   Elegant, Professional Blue. Smart Resume: empty sections auto-hide. */

const has = (v) => (Array.isArray(v) ? v.length > 0 : !!(v && String(v).trim()));

const contactLine = (p) => [p.email, p.mobile, p.city].filter(Boolean).join("  ·  ");

const ExpItem = ({ exp, compact }) => (
  <div>
    <div className="flex items-baseline justify-between gap-2">
      <p className={`font-semibold ${compact ? "text-[13px]" : "text-sm"} text-slate-900`}>{exp.title}</p>
      <p className="text-xs text-slate-500 shrink-0">{exp.start} – {exp.current ? "Present" : exp.end || "—"}</p>
    </div>
    <p className="text-sm text-slate-600">{exp.company}</p>
    {has(exp.description) && <p className="text-xs text-slate-600 mt-1 leading-relaxed whitespace-pre-line">{exp.description}</p>}
  </div>
);

const EduItem = ({ edu }) => (
  <div className="flex items-baseline justify-between gap-2">
    <div>
      <p className="font-semibold text-sm text-slate-900">{edu.degree}</p>
      <p className="text-sm text-slate-600">{edu.institution}{edu.grade ? ` · ${edu.grade}` : ""}</p>
    </div>
    <p className="text-xs text-slate-500 shrink-0">{edu.year}</p>
  </div>
);

const Pills = ({ items, className = "" }) => (
  <div className="flex flex-wrap gap-1.5 mt-2">
    {items.map((s, i) => (
      <span key={i} className={`text-xs border rounded-full px-2.5 py-0.5 ${className || "border-slate-300 text-slate-700"}`}>{s}</span>
    ))}
  </div>
);

/* ---------- 1. Classic ---------- */
const ClassicSection = ({ title, children }) => (
  <section className="mt-5">
    <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 border-b border-slate-200 pb-1">{title}</h2>
    <div className="mt-2.5">{children}</div>
  </section>
);

const ClassicTemplate = ({ r }) => {
  const p = r.personal || {};
  return (
    <div className="p-8 sm:p-10">
      <header className="text-center border-b-2 border-slate-800 pb-4">
        <h1 className="text-3xl font-heading font-bold text-slate-900">{p.name || "Your Name"}</h1>
        {has(contactLine(p)) && <p className="text-sm text-slate-600 mt-1.5">{contactLine(p)}</p>}
      </header>
      {has(r.objective) && <ClassicSection title="Career Objective"><p className="text-sm text-slate-700 leading-relaxed">{r.objective}</p></ClassicSection>}
      {has(r.experience) && <ClassicSection title="Experience"><div className="space-y-3.5">{r.experience.map((e, i) => <ExpItem key={i} exp={e} />)}</div></ClassicSection>}
      {has(r.education) && <ClassicSection title="Education"><div className="space-y-2.5">{r.education.map((e, i) => <EduItem key={i} edu={e} />)}</div></ClassicSection>}
      {has(r.skills) && <ClassicSection title="Skills"><Pills items={r.skills} /></ClassicSection>}
      {has(r.languages) && <ClassicSection title="Languages"><Pills items={r.languages} /></ClassicSection>}
    </div>
  );
};

/* ---------- 2. Modern (blue sidebar) ---------- */
const SideTitle = ({ children }) => <h2 className="text-[11px] font-bold uppercase tracking-widest text-blue-100 mt-6 first:mt-0">{children}</h2>;
const MainTitle = ({ children }) => <h2 className="text-xs font-bold uppercase tracking-widest text-blue-700 mt-6 first:mt-0">{children}</h2>;

const ModernTemplate = ({ r }) => {
  const p = r.personal || {};
  return (
    <div className="flex min-h-full" style={{ printColorAdjust: "exact", WebkitPrintColorAdjust: "exact" }}>
      <aside className="w-[35%] bg-blue-700 text-white p-6">
        <h1 className="text-2xl font-heading font-bold leading-tight">{p.name || "Your Name"}</h1>
        <div className="mt-4 space-y-1 text-xs text-blue-100">
          {p.mobile && <p>{p.mobile}</p>}
          {p.email && <p className="break-all">{p.email}</p>}
          {p.city && <p>{p.city}</p>}
        </div>
        {has(r.skills) && (<><SideTitle>Skills</SideTitle><ul className="mt-2 space-y-1">{r.skills.map((s, i) => <li key={i} className="text-xs">• {s}</li>)}</ul></>)}
        {has(r.languages) && (<><SideTitle>Languages</SideTitle><ul className="mt-2 space-y-1">{r.languages.map((s, i) => <li key={i} className="text-xs">• {s}</li>)}</ul></>)}
      </aside>
      <main className="flex-1 p-6">
        {has(r.objective) && (<><MainTitle>Career Objective</MainTitle><p className="text-sm text-slate-700 mt-2 leading-relaxed">{r.objective}</p></>)}
        {has(r.experience) && (<><MainTitle>Experience</MainTitle><div className="space-y-3.5 mt-2">{r.experience.map((e, i) => <ExpItem key={i} exp={e} compact />)}</div></>)}
        {has(r.education) && (<><MainTitle>Education</MainTitle><div className="space-y-2.5 mt-2">{r.education.map((e, i) => <EduItem key={i} edu={e} />)}</div></>)}
      </main>
    </div>
  );
};

/* ---------- 3. Minimal ---------- */
const MinSection = ({ title, children }) => (
  <section className="mt-6">
    <h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">{title}</h2>
    <div className="mt-2">{children}</div>
  </section>
);

const MinimalTemplate = ({ r }) => {
  const p = r.personal || {};
  return (
    <div className="p-8 sm:p-10">
      <header>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight">{p.name || "Your Name"}</h1>
        {has(contactLine(p)) && <p className="text-sm text-slate-500 mt-1">{contactLine(p)}</p>}
        <div className="h-px bg-slate-900 mt-4" />
      </header>
      {has(r.objective) && <MinSection title="Objective"><p className="text-sm text-slate-700 leading-relaxed">{r.objective}</p></MinSection>}
      {has(r.experience) && <MinSection title="Experience"><div className="space-y-3.5">{r.experience.map((e, i) => <ExpItem key={i} exp={e} />)}</div></MinSection>}
      {has(r.education) && <MinSection title="Education"><div className="space-y-2.5">{r.education.map((e, i) => <EduItem key={i} edu={e} />)}</div></MinSection>}
      {has(r.skills) && <MinSection title="Skills"><p className="text-sm text-slate-700">{r.skills.join("  ·  ")}</p></MinSection>}
      {has(r.languages) && <MinSection title="Languages"><p className="text-sm text-slate-700">{r.languages.join("  ·  ")}</p></MinSection>}
    </div>
  );
};

/* ---------- 4. Executive (dark header band, serif accents) ---------- */
const ExecSection = ({ title, children }) => (
  <section className="mt-5">
    <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-slate-800">{title}</h2>
    <div className="h-0.5 w-10 bg-amber-500 mt-1" />
    <div className="mt-2.5">{children}</div>
  </section>
);

const ExecutiveTemplate = ({ r }) => {
  const p = r.personal || {};
  return (
    <div style={{ printColorAdjust: "exact", WebkitPrintColorAdjust: "exact" }}>
      <header className="bg-slate-900 text-white px-8 py-6">
        <h1 className="text-3xl font-heading font-bold tracking-tight">{p.name || "Your Name"}</h1>
        {has(contactLine(p)) && <p className="text-sm text-slate-300 mt-1.5">{contactLine(p)}</p>}
      </header>
      <div className="px-8 py-4">
        {has(r.objective) && <ExecSection title="Profile"><p className="text-sm text-slate-700 leading-relaxed">{r.objective}</p></ExecSection>}
        {has(r.experience) && <ExecSection title="Experience"><div className="space-y-3.5">{r.experience.map((e, i) => <ExpItem key={i} exp={e} />)}</div></ExecSection>}
        {has(r.education) && <ExecSection title="Education"><div className="space-y-2.5">{r.education.map((e, i) => <EduItem key={i} edu={e} />)}</div></ExecSection>}
        {has(r.skills) && <ExecSection title="Core Skills"><Pills items={r.skills} className="border-amber-300 bg-amber-50 text-slate-800" /></ExecSection>}
        {has(r.languages) && <ExecSection title="Languages"><p className="text-sm text-slate-700">{r.languages.join("  ·  ")}</p></ExecSection>}
      </div>
    </div>
  );
};

/* ---------- 5. ATS Friendly (plain, single column, machine readable) ---------- */
const AtsSection = ({ title, children }) => (
  <section className="mt-4">
    <h2 className="text-sm font-bold text-black uppercase">{title}</h2>
    <div className="mt-1.5">{children}</div>
  </section>
);

const AtsTemplate = ({ r }) => {
  const p = r.personal || {};
  return (
    <div className="p-8 sm:p-10 font-body">
      <header>
        <h1 className="text-2xl font-bold text-black">{p.name || "Your Name"}</h1>
        {has(contactLine(p)) && <p className="text-sm text-black mt-1">{contactLine(p)}</p>}
      </header>
      {has(r.objective) && <AtsSection title="Career Objective"><p className="text-sm text-black leading-relaxed">{r.objective}</p></AtsSection>}
      {has(r.experience) && (
        <AtsSection title="Work Experience">
          <div className="space-y-3">
            {r.experience.map((e, i) => (
              <div key={i}>
                <p className="text-sm font-bold text-black">{e.title} — {e.company}</p>
                <p className="text-xs text-black">{e.start} – {e.current ? "Present" : e.end || ""}</p>
                {has(e.description) && <p className="text-sm text-black mt-0.5 whitespace-pre-line">{e.description}</p>}
              </div>
            ))}
          </div>
        </AtsSection>
      )}
      {has(r.education) && (
        <AtsSection title="Education">
          <div className="space-y-1.5">
            {r.education.map((e, i) => (
              <p key={i} className="text-sm text-black">{e.degree} — {e.institution}{e.year ? `, ${e.year}` : ""}{e.grade ? ` (${e.grade})` : ""}</p>
            ))}
          </div>
        </AtsSection>
      )}
      {has(r.skills) && <AtsSection title="Skills"><p className="text-sm text-black">{r.skills.join(", ")}</p></AtsSection>}
      {has(r.languages) && <AtsSection title="Languages"><p className="text-sm text-black">{r.languages.join(", ")}</p></AtsSection>}
    </div>
  );
};

/* ---------- 6. Corporate (gray banner + two-column body) ---------- */
const CorpTitle = ({ children }) => <h2 className="text-xs font-bold uppercase tracking-widest text-slate-700 mt-5 first:mt-0 flex items-center gap-2"><span className="h-1.5 w-1.5 bg-slate-700 inline-block" />{children}</h2>;

const CorporateTemplate = ({ r }) => {
  const p = r.personal || {};
  return (
    <div style={{ printColorAdjust: "exact", WebkitPrintColorAdjust: "exact" }}>
      <header className="bg-slate-100 border-b-4 border-slate-700 px-8 py-6">
        <h1 className="text-3xl font-heading font-bold text-slate-900">{p.name || "Your Name"}</h1>
        {has(contactLine(p)) && <p className="text-sm text-slate-600 mt-1">{contactLine(p)}</p>}
      </header>
      <div className="grid grid-cols-3 gap-6 px-8 py-5">
        <div className="col-span-2">
          {has(r.objective) && (<><CorpTitle>Summary</CorpTitle><p className="text-sm text-slate-700 mt-2 leading-relaxed">{r.objective}</p></>)}
          {has(r.experience) && (<><CorpTitle>Experience</CorpTitle><div className="space-y-3.5 mt-2">{r.experience.map((e, i) => <ExpItem key={i} exp={e} compact />)}</div></>)}
          {has(r.education) && (<><CorpTitle>Education</CorpTitle><div className="space-y-2.5 mt-2">{r.education.map((e, i) => <EduItem key={i} edu={e} />)}</div></>)}
        </div>
        <div>
          {has(r.skills) && (<><CorpTitle>Skills</CorpTitle><ul className="mt-2 space-y-1">{r.skills.map((s, i) => <li key={i} className="text-xs text-slate-700 border-b border-slate-100 pb-1">{s}</li>)}</ul></>)}
          {has(r.languages) && (<><CorpTitle>Languages</CorpTitle><ul className="mt-2 space-y-1">{r.languages.map((s, i) => <li key={i} className="text-xs text-slate-700">{s}</li>)}</ul></>)}
        </div>
      </div>
    </div>
  );
};

/* ---------- 7. Elegant (centered, thin rules, serif feel) ---------- */
const ElegantSection = ({ title, children }) => (
  <section className="mt-6 text-center">
    <h2 className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-500">{title}</h2>
    <div className="w-8 h-px bg-slate-300 mx-auto mt-1.5" />
    <div className="mt-3 text-left">{children}</div>
  </section>
);

const ElegantTemplate = ({ r }) => {
  const p = r.personal || {};
  return (
    <div className="p-8 sm:p-10">
      <header className="text-center">
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-wide">{p.name || "Your Name"}</h1>
        {has(contactLine(p)) && <p className="text-xs text-slate-500 mt-2 tracking-wider">{contactLine(p)}</p>}
        <div className="flex items-center justify-center gap-3 mt-4">
          <span className="h-px w-16 bg-slate-300" /><span className="h-1.5 w-1.5 rotate-45 bg-slate-400" /><span className="h-px w-16 bg-slate-300" />
        </div>
      </header>
      {has(r.objective) && <ElegantSection title="Objective"><p className="text-sm text-slate-700 leading-relaxed text-center">{r.objective}</p></ElegantSection>}
      {has(r.experience) && <ElegantSection title="Experience"><div className="space-y-3.5">{r.experience.map((e, i) => <ExpItem key={i} exp={e} />)}</div></ElegantSection>}
      {has(r.education) && <ElegantSection title="Education"><div className="space-y-2.5">{r.education.map((e, i) => <EduItem key={i} edu={e} />)}</div></ElegantSection>}
      {has(r.skills) && <ElegantSection title="Skills"><div className="flex flex-wrap gap-1.5 justify-center">{r.skills.map((s, i) => <span key={i} className="text-xs border border-slate-300 text-slate-700 rounded-full px-2.5 py-0.5">{s}</span>)}</div></ElegantSection>}
      {has(r.languages) && <ElegantSection title="Languages"><p className="text-sm text-slate-700 text-center">{r.languages.join("  ·  ")}</p></ElegantSection>}
    </div>
  );
};

/* ---------- 8. Professional Blue (top band + blue accents) ---------- */
const BlueSection = ({ title, children }) => (
  <section className="mt-5">
    <h2 className="text-xs font-bold uppercase tracking-widest text-white bg-blue-600 inline-block px-2.5 py-1 rounded-sm">{title}</h2>
    <div className="mt-2.5">{children}</div>
  </section>
);

const ProfessionalBlueTemplate = ({ r }) => {
  const p = r.personal || {};
  return (
    <div style={{ printColorAdjust: "exact", WebkitPrintColorAdjust: "exact" }}>
      <div className="h-2.5 bg-blue-600" />
      <div className="p-8 sm:p-10 pt-6">
        <header className="flex items-baseline justify-between gap-3 border-b border-blue-100 pb-4">
          <h1 className="text-3xl font-heading font-bold text-blue-700">{p.name || "Your Name"}</h1>
          {has(contactLine(p)) && <p className="text-xs text-slate-600 text-right">{contactLine(p)}</p>}
        </header>
        {has(r.objective) && <BlueSection title="Career Objective"><p className="text-sm text-slate-700 leading-relaxed">{r.objective}</p></BlueSection>}
        {has(r.experience) && <BlueSection title="Experience"><div className="space-y-3.5">{r.experience.map((e, i) => <ExpItem key={i} exp={e} />)}</div></BlueSection>}
        {has(r.education) && <BlueSection title="Education"><div className="space-y-2.5">{r.education.map((e, i) => <EduItem key={i} edu={e} />)}</div></BlueSection>}
        {has(r.skills) && <BlueSection title="Skills"><Pills items={r.skills} className="border-blue-200 bg-blue-50 text-blue-800" /></BlueSection>}
        {has(r.languages) && <BlueSection title="Languages"><Pills items={r.languages} className="border-blue-200 text-slate-700" /></BlueSection>}
      </div>
    </div>
  );
};

export const TEMPLATES = [
  { key: "classic", label: "Classic", component: ClassicTemplate },
  { key: "modern", label: "Modern", component: ModernTemplate },
  { key: "minimal", label: "Minimal", component: MinimalTemplate },
  { key: "executive", label: "Executive", component: ExecutiveTemplate },
  { key: "ats", label: "ATS Friendly", component: AtsTemplate },
  { key: "corporate", label: "Corporate", component: CorporateTemplate },
  { key: "elegant", label: "Elegant", component: ElegantTemplate },
  { key: "professional_blue", label: "Professional Blue", component: ProfessionalBlueTemplate },
];

export const TemplateRenderer = ({ resume }) => {
  const tpl = TEMPLATES.find((t) => t.key === (resume.template || "classic")) || TEMPLATES[0];
  const Component = tpl.component;
  return <Component r={resume} />;
};
